// src/util/pricing.js
import { types as sdkTypes } from '../util/sdkLoader';
import { formatMoney } from './currency';

const { Money } = sdkTypes;

/** ---------- Common (shared) helpers ---------- **/

/** Resolve buyer-protection opts from config (defaults to 11%) */
export const resolveBuyerProtectionOptions = config => {
  const percent =
    typeof config?.buyerProtectionPercent === 'number' ? config.buyerProtectionPercent : 0.11;
  const fixedSubunits =
    typeof config?.buyerProtectionFixedSubunits === 'number'
      ? config.buyerProtectionFixedSubunits
      : 0;
  const minSubunits =
    typeof config?.buyerProtectionMinSubunits === 'number'
      ? config?.buyerProtectionMinSubunits
      : 0;
  return { percent, fixedSubunits, minSubunits };
};

/** Fee = round(amount * percent) + fixedSubunits, clamped to minSubunits */
export const computeBuyerProtectionFee = (money, { percent, fixedSubunits, minSubunits }) => {
  if (!(money instanceof Money)) return null;
  const pctPart = Math.round(money.amount * (percent || 0));
  const raw = pctPart + (fixedSubunits || 0);
  const feeSubunits = Math.max(raw, minSubunits || 0);
  return new Money(feeSubunits, money.currency);
};

/** ---------- Listing shape helpers (existing) ---------- **/

export const getListingPrice = listing => {
  const p = listing?.attributes?.price;
  return p instanceof Money ? p : null;
};

export const getListingPriceTotals = (listing, config) => {
  const base = getListingPrice(listing);
  if (!(base instanceof Money)) return { base: null, fee: null, total: null };

  const opts = resolveBuyerProtectionOptions(config);
  const fee = computeBuyerProtectionFee(base, opts);
  const total = new Money(base.amount + (fee?.amount || 0), base.currency);
  return { base, fee, total };
};

/** ---------- Cart item shape helpers (NEW) ---------- **/

/** Safely extract Money from a cart item like { price, listingId, title, ... } */
export const getCartItemPrice = cartItem => {
  const p = cartItem?.price;
  return p instanceof Money ? p : null;
};

export const getCartItemPriceTotals = (cartItem, config) => {
  const base = getCartItemPrice(cartItem);
  if (!(base instanceof Money)) return { base: null, fee: null, total: null };

  const opts = resolveBuyerProtectionOptions(config);
  const fee = computeBuyerProtectionFee(base, opts);
  const total = new Money(base.amount + (fee?.amount || 0), base.currency);
  return { base, fee, total };
};

/** Convenience formatter for cart items */
export const formatCartItemTotals = (intl, cartItem, config, { which = 'base' } = {}) => {
  const totals = getCartItemPriceTotals(cartItem, config);
  const val = totals[which];
  return val ? formatMoney(intl, val) : null;
};
